/**
 * WeakMap-based memoization helpers for pure engine functions.
 *
 * Implements `05-engine-types-and-interfaces.md` §G.1.
 *
 * Key by reference (===), not by value. Caller is responsible for ensuring
 * the same logical input produces the same reference (i.e., reuse Snapshot
 * instances across calls when nothing changed — the adapter's
 * RevisionTracker drives this).
 *
 * The caches are WeakMap-backed, so unreferenced keys are garbage-collected
 * when the snapshot/graph becomes unreachable (next mutation cycle).
 */

/**
 * Single-argument memoization. Used for `buildAnalysisGraph(snapshot)`,
 * `analyze(snapshot)`, `topologicalSort(graph)`, etc.
 */
export function memoize1<K extends object, V>(fn: (k: K) => V): (k: K) => V {
  const cache = new WeakMap<K, V>();
  return (k: K): V => {
    const hit = cache.get(k);
    if (hit !== undefined) return hit;
    const value = fn(k);
    cache.set(k, value);
    return value;
  };
}

/**
 * Two-argument memoization. Outer WeakMap on the first key (must be an
 * object); inner cache for the second key.
 *
 * When the second key is a primitive (TaskID = string | number), the inner
 * is a Map.
 * When the second key is an object reference, the inner is a WeakMap.
 *
 * Used for `buildSchedulingGraph(snapshot, config)`,
 * `getConstraintLimits(snapshot, taskId)`, `resolveConstraintType(snapshot, taskId)`.
 */
export function memoize2<K1 extends object, K2 extends object, V>(
  fn: (k1: K1, k2: K2) => V,
  innerKind: "weakmap"
): (k1: K1, k2: K2) => V;
export function memoize2<K1 extends object, K2, V>(
  fn: (k1: K1, k2: K2) => V,
  innerKind?: "map"
): (k1: K1, k2: K2) => V;
export function memoize2<K1 extends object, K2, V>(
  fn: (k1: K1, k2: K2) => V,
  innerKind: "map" | "weakmap" = "map"
): (k1: K1, k2: K2) => V {
  const outer = new WeakMap<K1, Map<K2, V> | WeakMap<object, V>>();
  return (k1: K1, k2: K2): V => {
    let inner = outer.get(k1);
    if (!inner) {
      inner = innerKind === "weakmap"
        ? new WeakMap<object, V>()
        : new Map<K2, V>();
      outer.set(k1, inner);
    }
    // Type narrowing: we know inner matches innerKind by construction.
    const hit = innerKind === "weakmap"
      ? (inner as WeakMap<object, V>).get(k2 as unknown as object)
      : (inner as Map<K2, V>).get(k2);
    if (hit !== undefined) return hit;
    const value = fn(k1, k2);
    if (innerKind === "weakmap") {
      (inner as WeakMap<object, V>).set(k2 as unknown as object, value);
    } else {
      (inner as Map<K2, V>).set(k2, value);
    }
    return value;
  };
}
